import { useState, useCallback, useMemo } from 'react';
import {
  Tabs, Select, DatePicker, Card, Statistic, Row, Col, Button, Slider,
  Table, InputNumber, Input, message, Spin, Tag, Alert, Tooltip,
} from 'antd';
import {
  PlayCircleOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import type { EChartsOption } from 'echarts';
import ChartCard from '@/components/ChartCard';
import PageContainer from '@/components/PageContainer';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useStations } from '@/hooks/useStations';
import {
  runHistorical, runForecast,
  HistoricalRequest, HistoricalResult, EpochDetail,
  ForecastResult, ForecastRequest,
} from '@/api/optimization';
import { BASE_DATAZOOM, format2 } from '@/utils/charts';

const { TextArea } = Input;

export default function OptimizationPage() {
  const { data: stationData } = useStations();
  const stations = stationData?.stations ?? [];

  return (
    <PageContainer title="日前优化申报">
      <Tabs
        defaultActiveKey="historical"
        items={[
          {
            key: 'historical',
            label: '历史回测',
            children: <HistoricalTab stations={stations} />,
          },
          {
            key: 'forecast',
            label: '事前决策',
            children: <ForecastTab stations={stations} />,
          },
        ]}
      />
    </PageContainer>
  );
}

// ─── Historical Tab ───

function HistoricalTab({ stations }: { stations: string[] }) {
  const isMobile = useIsMobile();
  const [station, setStation] = useState(stations[0] ?? '');
  const [date, setDate] = useState(dayjs().subtract(1, 'day'));
  const [socPct, setSocPct] = useState(10);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<HistoricalResult | null>(null);

  const handleRun = useCallback(async () => {
    if (!station || !date) return;
    setLoading(true);
    try {
      const req: HistoricalRequest = {
        station,
        date: date.format('YYYY-MM-DD'),
        soc_initial_pct: socPct / 100,
      };
      const r = await runHistorical(req);
      setResult(r);
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? '回测失败');
    } finally {
      setLoading(false);
    }
  }, [station, date, socPct]);

  const diffCols: ColumnsType<EpochDetail> = [
    { title: '时段', dataIndex: 'slot', width: 60, render: (v: number) => `${v}` },
    { title: '时间', dataIndex: 'time', width: 70 },
    { title: '实际申报(MW)', dataIndex: 'actual_ep', width: 120, render: (v: number) => format2(v) },
    { title: '最优申报(MW)', dataIndex: 'optimal_ep', width: 120, render: (v: number) => format2(v) },
    {
      title: '差异(MW)', dataIndex: 'diff', width: 100,
      render: (v: number) => <span style={{ color: v > 0 ? '#16a34a' : '#dc2626' }}>{v > 0 ? '+' : ''}{format2(v)}</span>,
    },
  ];

  const timeLabels = useMemo(() =>
    Array.from({ length: 96 }, (_, i) =>
      `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`
    ), []
  );

  const epPriceChart: EChartsOption | null = useMemo(() => result ? {
    tooltip: { trigger: 'axis' },
    legend: { data: ['实际ep', '最优ep', '实时电价', '光伏可用', 'AGC目标', 'AGC目标(改)'], bottom: 0 },
    grid: { top: 10, left: 60, right: 60, bottom: 30 },
    xAxis: { data: timeLabels, axisLabel: { interval: 7 } },
    yAxis: [
      { name: '功率(MW)', type: 'value' },
      { name: '电价(元/MWh)', type: 'value' },
    ],
    dataZoom: BASE_DATAZOOM as any,
    series: [
      { name: '实际ep', type: 'bar', data: result.actual.ep, yAxisIndex: 0, itemStyle: { color: '#94a3b8' }, barWidth: 4 },
      { name: '最优ep', type: 'bar', data: result.optimal.ep, yAxisIndex: 0, itemStyle: { color: '#3b82f6' }, barWidth: 4 },
      { name: '实时电价', type: 'line', data: result.price_rt, yAxisIndex: 1, lineStyle: { color: '#ef4444' }, symbol: 'none' },
      { name: '光伏可用', type: 'line', data: result.pv_avail, yAxisIndex: 0, lineStyle: { color: '#f59e0b', type: 'dashed' }, symbol: 'none' },
      { name: 'AGC目标', type: 'line', data: result.agc_target, yAxisIndex: 0, lineStyle: { color: '#9ca3af', width: 1 }, symbol: 'none' },
      { name: 'AGC目标(改)', type: 'line', data: result.agc_target_opt, yAxisIndex: 0, lineStyle: { color: '#06b6d4', width: 2 }, symbol: 'none' },
    ],
  } : null, [result, timeLabels]);

  const hourLabels = useMemo(() =>
    Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`), []
  );

  const waterfallChart: EChartsOption | null = useMemo(() => {
    if (!result) return null;
    const { actual, optimal } = result;
    const diffs = actual.hourly_revenue.map((a, i) => optimal.hourly_revenue[i] - a);
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const h = params[0]?.name ?? '';
          const a = actual.hourly_revenue[params[0]?.dataIndex ?? 0] ?? 0;
          const o = optimal.hourly_revenue[params[0]?.dataIndex ?? 0] ?? 0;
          return `${h}<br/>实际: ${format2(a)} 元<br/>最优: ${format2(o)} 元<br/>差额: ${(o - a) > 0 ? '+' : ''}${format2(o - a)} 元`;
        },
      },
      legend: { data: ['实际电费', '额外增收'], bottom: 0 },
      grid: { top: 10, left: 60, right: 10, bottom: 30 },
      xAxis: { data: hourLabels, axisLabel: { interval: 2 } },
      yAxis: { name: '元', type: 'value' },
      dataZoom: BASE_DATAZOOM as any,
      series: [
        {
          name: '实际电费', type: 'bar', stack: 'total',
          data: actual.hourly_revenue,
          itemStyle: { color: '#9ca3af' },
          barWidth: 14,
        },
        {
          name: '额外增收', type: 'bar', stack: 'total',
          data: diffs.map(d => Math.max(0, d)),
          itemStyle: { color: '#16a34a' },
          barWidth: 14,
        },
      ],
    };
  }, [result, hourLabels]);

  const chartH = isMobile ? 250 : 400;

  return (
    <Spin spinning={loading} tip="正在求解...">
      <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
        <Col xs={24} sm={12} md={6}>
          <Select
            style={{ width: '100%' }}
            placeholder="选择电站"
            value={station || undefined}
            options={stations.map(s => ({ value: s, label: s }))}
            onChange={setStation}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <DatePicker
            style={{ width: '100%' }}
            value={date}
            onChange={d => setDate(d ?? dayjs())}
            allowClear={false}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Tooltip title={`初始 SOC: ${socPct}% (${result ? (socPct / 100 * result.bat_capacity).toFixed(1) : '-'} MWh)`}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>初始 SOC</span>
            <Slider
              min={5}
              max={95}
              step={1}
              value={socPct}
              onChange={setSocPct}
              marks={{ 5: '5%', 50: '50%', 95: '95%' }}
            />
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleRun} block>
            开始回测
          </Button>
        </Col>
      </Row>

      {result && (
        <>
          <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="实际净收益" value={result.actual.net_revenue} precision={0} suffix="元" valueStyle={{ color: '#374151' }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="最优净收益" value={result.optimal.net_revenue} precision={0} suffix="元" valueStyle={{ color: '#16a34a' }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="改善" value={result.improvement} precision={0} suffix="元" valueStyle={{ color: result.improvement > 0 ? '#dc2626' : '#16a34a' }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="求解" value={result.solve_time} precision={1} suffix="秒" />
                <Tag color={result.status === 'Optimal' ? 'green' : 'orange'} style={{ marginTop: 4 }}>{result.status}</Tag>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="储能放电"
                  value={result.actual.bat_discharge}
                  precision={0}
                  suffix={`→ ${result.optimal.bat_discharge} MWh`}
                  valueStyle={{ color: '#dc2626', fontSize: isMobile ? 16 : 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="储能充电"
                  value={result.actual.bat_charge}
                  precision={0}
                  suffix={`→ ${result.optimal.bat_charge} MWh`}
                  valueStyle={{ color: '#2563eb', fontSize: isMobile ? 16 : 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="弃电"
                  value={result.actual.curtailment}
                  precision={0}
                  suffix={`→ ${result.optimal.curtailment} MWh`}
                  valueStyle={{ color: '#d97706', fontSize: isMobile ? 16 : 20 }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="末态SOC"
                  value={result.actual.soc_final}
                  precision={0}
                  suffix={`→ ${result.optimal.soc_final} MWh`}
                  valueStyle={{ color: '#7c3aed', fontSize: isMobile ? 16 : 20 }}
                />
              </Card>
            </Col>
          </Row>

          {epPriceChart && <ChartCard title="申报方案 + 实时电价" option={epPriceChart} height={chartH} />}

          {waterfallChart && <ChartCard title="逐小时电费瀑布图" option={waterfallChart} height={chartH} />}

          <Card title="申报差异 TOP 10" size="small" style={{ marginTop: 16 }}>
            <Table dataSource={result.top_diffs} columns={diffCols} rowKey="slot" pagination={false} size="small" scroll={isMobile ? { x: 450 } : undefined} />
          </Card>
        </>
      )}
    </Spin>
  );
}

// ─── Forecast Tab ───

function ForecastTab({ stations }: { stations: string[] }) {
  const isMobile = useIsMobile();
  const [station, setStation] = useState(stations[0] ?? '');
  const [targetDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [pvText, setPvText] = useState('');
  const [curtailmentInput, setCurtailmentInput] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ForecastResult | null>(null);
  const [parseError, setParseError] = useState('');

  const parsePvForecast = useCallback((): number[] | null => {
    if (!pvText.trim()) {
      setParseError('请输入96点PV预测数据');
      return null;
    }
    try {
      const arr = JSON.parse(pvText.trim());
      if (!Array.isArray(arr)) {
        setParseError('输入必须是数组格式');
        return null;
      }
      if (arr.length !== 96) {
        setParseError(`需要96个数值，当前${arr.length}个`);
        return null;
      }
      setParseError('');
      return arr.map(Number);
    } catch {
      setParseError('JSON 解析失败，请检查格式');
      return null;
    }
  }, [pvText]);

  const handleRun = useCallback(async () => {
    const pv = parsePvForecast();
    if (!pv || !station) return;

    setLoading(true);
    try {
      const req: ForecastRequest = {
        station,
        pv_forecast: pv,
        target_date: targetDate,
      };
      if (curtailmentInput !== null) {
        req.curtailment_rate = Array(96).fill(curtailmentInput);
      }
      const r = await runForecast(req);
      setResult(r);
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? '求解失败');
    } finally {
      setLoading(false);
    }
  }, [parsePvForecast, station, targetDate, curtailmentInput]);

  const scenarioCols: ColumnsType<ForecastResult['scenarios'][0]> = [
    { title: '场景', dataIndex: 'name', width: 200 },
    { title: '净收益', dataIndex: 'net_revenue', render: (v: number) => `${format2(v)} 元` },
    { title: '考核', dataIndex: 'penalty', render: (v: number) => `${format2(v)} 元` },
    { title: '末态SOC', dataIndex: 'soc_final', render: (v: number) => `${format2(v)} MWh` },
  ];

  const forecastChart: EChartsOption | null = result
    ? {
        tooltip: { trigger: 'axis' },
        legend: { data: ['推荐ep', '电价预测', 'PV预测'], bottom: 0 },
        grid: { top: 10, left: 60, right: 60, bottom: 30 },
        xAxis: { data: Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`), axisLabel: { interval: 7 } },
        yAxis: [
          { name: '功率(MW)', type: 'value' },
          { name: '电价(元/MWh)', type: 'value' },
        ],
        dataZoom: BASE_DATAZOOM as any,
        series: [
          { name: '推荐ep', type: 'bar', data: result.ep, yAxisIndex: 0, itemStyle: { color: '#2563eb' } },
          { name: 'PV预测', type: 'line', data: result.pv_forecast ?? [], yAxisIndex: 0, lineStyle: { color: '#f59e0b', type: 'dashed' as const } },
          { name: '电价预测', type: 'line', data: result.price_forecast, yAxisIndex: 1, lineStyle: { color: '#dc2626' } },
        ],
      }
    : null;

  const chartH = isMobile ? 250 : 400;

  return (
    <Spin spinning={loading} tip="正在求解（含敏感性分析，约需1分钟）...">
      <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Select
            style={{ width: '100%' }}
            placeholder="选择电站"
            value={station || undefined}
            options={stations.map(s => ({ value: s, label: s }))}
            onChange={setStation}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Tooltip title="默认从历史数据自动预测，可手动覆盖">
            <InputNumber
              style={{ width: '100%' }}
              placeholder="限电率 (0~1)"
              min={0}
              max={1}
              step={0.01}
              value={curtailmentInput}
              onChange={v => setCurtailmentInput(v)}
              addonAfter={<InfoCircleOutlined />}
            />
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleRun} block>
            开始求解
          </Button>
        </Col>
      </Row>

      <Alert
        type="info"
        message="96点PV预测数据输入"
        description={
          <div>
            <TextArea
              rows={isMobile ? 3 : 5}
              placeholder={'粘贴 JSON 数组，如 [0, 0, 0, 0, 1.5, 3.2, ...]'}
              value={pvText}
              onChange={e => setPvText(e.target.value)}
            />
            {parseError && <div style={{ color: '#dc2626', marginTop: 4 }}>{parseError}</div>}
          </div>
        }
        style={{ marginBottom: 16 }}
      />

      {result && (
        <>
          <Row gutter={[16, 8]} style={{ marginBottom: 8 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="预测净收益" value={result.net_revenue} precision={0} suffix="元" valueStyle={{ color: '#16a34a' }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="预测考核" value={result.penalty} precision={0} suffix="元" valueStyle={{ color: result.penalty > 0 ? '#dc2626' : '#374151' }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="总上网电量" value={result.total_grid} precision={1} suffix="MWh" />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="求解耗时" value={result.solve_time} precision={1} suffix="秒" />
              </Card>
            </Col>
          </Row>

          {result.risk_notes.length > 0 && (
            <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
              {result.risk_notes.map((note, i) => (
                <Col xs={24} md={12} key={i}>
                  <Alert type={note.startsWith('警告') ? 'warning' : 'info'} message={note} />
                </Col>
              ))}
            </Row>
          )}

          {forecastChart && <ChartCard title="推荐申报方案" option={forecastChart} height={chartH} />}

          <Card title="敏感性分析" size="small" style={{ marginTop: 16 }}>
            <Table
              dataSource={result.scenarios}
              columns={scenarioCols}
              rowKey="name"
              pagination={false}
              size="small"
              scroll={isMobile ? { x: 500 } : undefined}
            />
          </Card>
        </>
      )}
    </Spin>
  );
}
